import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { itemType } from "../../../../../utility/data";
import { additems } from "../../../../../services/transaction";
import moment from "moment";
import { formatNumber } from "../../../../../utility/helper";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLegacies,
  resetLegaciesState,
} from "../../../../../redux/slices/legacySlice";
import {
  FormActions,
  FormDatePicker,
  FormDropdown,
  FormInput,
} from "../../../../../components/Stepper/fields/inputField";
import { handleErrorRedirect } from "../../../../../utility/errorRedirect";
import { useNavigate } from "react-router-dom";

const CreateTransactionModal = ({
  visible,
  actionTypes,
  fetchTransactionData,
  GetJournalData,
  GetLegaciesData,
  onHide,
  loading,
  setLoading,
  toast,
  assetName,
  GetAddedItemData,
}) => {
  const accessToken = JSON.parse(localStorage.getItem("access_token"));
  const { legaciesData, legacyLoading } = useSelector(
    (state) => state.legacies
  );

  const initialTransactionData = {
    date: moment().toDate(),
    itemName: "",
    itemType: itemType.length > 0 ? itemType[0].value : "",
    purchasePrice: "",
    legacy: legaciesData.length > 0 ? legaciesData[0] : "",
  };
  const [createTransactionData, setCreateTransactionData] = useState(
    initialTransactionData
  );
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchLegacies(accessToken)).then((resultAction) => {
        if (fetchLegacies.rejected.match(resultAction)) {
          const status = resultAction?.payload?.status;
          handleErrorRedirect(status, navigate);
        }
      });
    }
    return () => {
      dispatch(resetLegaciesState());
    };
  }, [dispatch, accessToken, navigate]);

  useEffect(() => {
    if (legaciesData?.length > 0 && !createTransactionData.legacy) {
      setCreateTransactionData((prevData) => ({
        ...prevData,
        legacy: legaciesData[0],
      }));
    }
  }, [legaciesData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target || e;

    setCreateTransactionData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name] && value && value.toString().trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!createTransactionData.date) {
      newErrors.date = "Date is required";
    }
    if (!createTransactionData.purchasePrice) {
      newErrors.purchasePrice = "Purchase Price is required";
    }

    if (!createTransactionData.itemName) {
      newErrors.itemName = "Item Name is required";
    }
    if (!createTransactionData.itemType) {
      newErrors.itemType = "Item type is required";
    }
    if (!createTransactionData.legacy) {
      newErrors.legacy = "legacy is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePurchasePriceChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    let rawValue = value.replace(/[^0-9.-]/g, "");

    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    value = formatNumber(rawValue);

    let AutoCalPL = null;
    AutoCalPL = formatNumber(Number(rawValue));

    setCreateTransactionData((prevData) => ({
      ...prevData,
      value: value,
      ...(AutoCalPL !== null && { purchasePrice: AutoCalPL }),
    }));

    if (errors[name] && value.trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handleSave = async () => {
    if (validateForm()) {
      setLoading(true);
      if (!assetName) {
        setLoading(false);
        toast.current.show({
          className: "bg-yellow-700 text-white",
          severity: "warn",
          summary: "Warning",
          detail: "Please add the first initial transaction",
          life: 3000,
        });
        return;
      }

      const formattedData = {
        ...createTransactionData,
        date: createTransactionData.date
          ? moment(createTransactionData.date).format("YYYY-MM-DD HH:mm:ss")
          : null,
        purchasePrice: createTransactionData.purchasePrice
          ? createTransactionData.purchasePrice.replace(/[,']/g, "")
          : "",
      };

      try {
        const response = await (actionTypes === "Create"
          ? additems(formattedData, assetName, accessToken)
          : "");
        if (response?.data?.status === true) {
          fetchTransactionData();
          GetAddedItemData();
          GetJournalData();
          GetLegaciesData();
          handleClose();
          toast.current.show({
            className: "bg-green-300 text-white",
            severity: "success",
            summary: "Success",
            detail: response?.data?.message,
            life: 3000,
          });
        }
      } catch (error) {
        const status = error?.response?.status;
        handleErrorRedirect(status, navigate);
        console.error("Error adding transaction:", error);
        toast.current.show({
          className: "bg-yellow-700 text-white",
          severity: "warn",
          summary: "Warning",
          detail: error.message,
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  if (!visible) return null;

  const handleClose = () => {
    setCreateTransactionData(initialTransactionData);
    setErrors({});
    onHide();
  };

  return (
    <div>
      <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
        <div className="dark:bg-custom-radial bg-[#F6F6F6] md:w-1/2 w-1/2 shadow-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-2xl text-lightPrimary text-black dark:text-primary-gradient">
              Add Item
            </h2>
            <FaTimes
              onClick={handleClose}
              className="cursor-pointer text-lightPrimary dark:text-darkWaring"
              fontSize={20}
            />
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <FormDropdown
                label="Item Type"
                name="itemType"
                value={createTransactionData.itemType}
                onChange={(e) =>
                  handleInputChange({ name: "itemType", value: e.value })
                }
                options={itemType}
                optionsLabel="name"
                error={errors.itemType}
                className={`rounded-lg w-full p-0 bg-transparent border  border-info dark:border-gray-300 text-white focus:outline-none focus:border-b-2 ${
                  createTransactionData.itemType ? "asset_Dropdown" : ""
                }`}
                placeholder="Select Item Type"
              />

              <FormDropdown
                label="Legacy*"
                name="legacy"
                value={createTransactionData.legacy}
                onChange={(e) =>
                  handleInputChange({ name: "legacy", value: e.value })
                }
                options={legaciesData}
                optionsLabel="name"
                error={errors.legacy}
                className={`w-full p-0 bg-transparent border  border-info dark:border-gray-300 text-white focus:outline-none focus:border-b-2 ${
                  createTransactionData.legacy ? "asset_Dropdown" : ""
                }`}
                placeholder="Select Legacy"
              />

              <FormInput
                label="Name*"
                name="itemName"
                value={createTransactionData.itemName}
                onChange={handleInputChange}
                error={errors.itemName}
              />

              <FormDatePicker
                label="Date*"
                name="date"
                value={createTransactionData.date}
                onChange={(e) =>
                  handleInputChange({ name: "date", value: e.value })
                }
                error={errors.date}
                minDate={null}
                maxDate={moment().toDate()}
                disabled={actionTypes === "Update"}
              />
              <FormInput
                label="Purchase Price*"
                name="purchasePrice"
                value={createTransactionData.purchasePrice || ""}
                onChange={handlePurchasePriceChange}
                error={errors.purchasePrice}
              />
            </div>

            <FormActions
              loading={loading}
              onCancel={handleClose}
              actionType={actionTypes}
              submitType="submit"
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTransactionModal;
